/**
 * Store Selector - Manages garden centre preference with localStorage
 * Uses subscription model for other elements to listen to changes
 */

// Prevent duplicate class declaration if script is loaded multiple times
if (typeof StoreSelector === 'undefined') {
class StoreSelector extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('[id^="store-selector-button-"]') || this.querySelector('.header-store-switcher__title');
    this.list = this.querySelector('[id^="store-selector-list-"]') || this.querySelector('.header-store-switcher__content');
    this.options = this.querySelectorAll('.store-selector__option').length > 0 
      ? this.querySelectorAll('.store-selector__option')
      : this.querySelectorAll('.header-store-switcher__item');
    this.selectedText = this.querySelector('[data-selected-text]');
    this.clearButton = this.querySelector('[data-clear-button]');
    this.isOpen = false;
    this.STORAGE_KEY = 'ygc_store_preference';
    
    this.init();
  }

  init() {
    if (!this.button || !this.list) return;
    
    this.initializeSelection();
    this.button.addEventListener('click', this.toggleDropdown.bind(this));
    this.button.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggleDropdown(e);
      }
    });
    this.options.forEach(option => {
      option.addEventListener('click', this.handleOptionClick.bind(this));
      option.addEventListener('keydown', this.handleOptionKeydown.bind(this));
    });
    
    if (this.clearButton) {
      this.clearButton.addEventListener('click', this.handleClearClick.bind(this));
    }
    
    document.addEventListener('click', this.handleOutsideClick.bind(this));
    document.addEventListener('storePreferenceChanged', this.handleStoreChange.bind(this));
  }

  initializeSelection() {
    // Don't auto-select in theme editor
    if (window.Shopify && window.Shopify.designMode) {
      return;
    }
    
    const storedValue = localStorage.getItem(this.STORAGE_KEY);
    
    if (storedValue) {
      const option = Array.from(this.options).find(
        opt => opt.dataset.storeValue === storedValue
      );
      if (option) {
        this.selectOption(option, false);
        return;
      }
    }
    
    this.resetToDefault();
  }

  toggleDropdown(event) {
    event.stopPropagation();
    this.isOpen = !this.isOpen;
    if (this.button) {
      this.button.setAttribute('aria-expanded', this.isOpen.toString());
    }
    if (this.list) {
      this.list.hidden = !this.isOpen;
    }
    
    // Toggle active class for new design
    if (this.classList.contains('header-store-switcher')) {
      if (this.isOpen) {
        this.classList.add('header-store-switcher--active');
      } else {
        this.classList.remove('header-store-switcher--active');
      }
    }
    
    if (this.isOpen) {
      const firstOption = this.options[0];
      if (firstOption) {
        setTimeout(() => firstOption.focus(), 0);
      }
    }
  }

  handleOptionClick(event) {
    event.stopPropagation();
    const option = event.currentTarget;
    this.selectOption(option);
    this.updateStorePreference(option.dataset.storeValue);
    this.closeDropdown();
  }

  handleOptionKeydown(event) {
    const option = event.currentTarget;
    const optionsArray = Array.from(this.options);
    const currentIndex = optionsArray.indexOf(option);
    
    switch(event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.selectOption(option);
        this.updateStorePreference(option.dataset.storeValue);
        this.closeDropdown();
        break;
      case 'ArrowDown':
        event.preventDefault();
        const nextIndex = (currentIndex + 1) % optionsArray.length;
        optionsArray[nextIndex].focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        const prevIndex = currentIndex === 0 ? optionsArray.length - 1 : currentIndex - 1;
        optionsArray[prevIndex].focus();
        break;
      case 'Escape':
        this.closeDropdown();
        this.button.focus();
        break;
    }
  }

  handleClearClick(event) {
    event.stopPropagation();
    this.clearSelection();
    this.closeDropdown();
  }

  handleOutsideClick(event) {
    if (!this.contains(event.target) && this.isOpen) {
      this.closeDropdown();
    }
  }

  handleStoreChange(event) {
    // Update selection if store changed externally
    const newValue = event.detail.value;
    if (newValue) {
      const option = Array.from(this.options).find(
        opt => opt.dataset.storeValue === newValue
      );
      if (option) {
        this.selectOption(option, false);
      }
    } else {
      this.resetToDefault();
    }
  }

  selectOption(option, updatePreference = true) {
    this.options.forEach(opt => {
      opt.classList.remove('store-selector__option--selected');
      opt.setAttribute('aria-selected', 'false');
    });
    option.classList.add('store-selector__option--selected');
    option.setAttribute('aria-selected', 'true');
    
    // Get text from option - support both old and new structure
    let optionText = option.querySelector('.store-selector__option-text');
    if (!optionText) {
      // New structure: text is in a span inside the item
      optionText = option.querySelector('span');
    }
    
    if (optionText && this.selectedText) {
      this.selectedText.textContent = optionText.textContent.trim();
    }
    
    if (this.clearButton) {
      this.clearButton.hidden = false;
    }
    
    if (updatePreference) {
      this.updateStorePreference(option.dataset.storeValue);
    }
  }

  resetToDefault() {
    this.options.forEach(opt => {
      opt.classList.remove('store-selector__option--selected');
      opt.setAttribute('aria-selected', 'false');
    });
    
    const defaultTextElement = this.querySelector('[data-default-text]');
    if (defaultTextElement && this.selectedText) {
      this.selectedText.textContent = defaultTextElement.textContent.trim();
    }
    
    if (this.clearButton) {
      this.clearButton.hidden = true;
    }
    
    // Remove active class if using new design
    if (this.classList.contains('header-store-switcher')) {
      this.classList.remove('header-store-switcher--active');
    }
  }

  clearSelection() {
    this.resetToDefault();
    this.updateStorePreference('');
  }

  updateStorePreference(value) {
    if (value && value !== '') {
      localStorage.setItem(this.STORAGE_KEY, value);
    } else {
      localStorage.removeItem(this.STORAGE_KEY);
    }
    
    const event = new CustomEvent('storePreferenceChanged', {
      detail: {
        value: value || '',
        storeName: value ? this.getStoreName(value) : ''
      },
      bubbles: true
    });
    document.dispatchEvent(event);
    StoreSelector.notifySubscribers(value);
  }

  getStoreName(value) {
    const option = Array.from(this.options).find(
      opt => opt.dataset.storeValue === value
    );
    if (option) {
      let optionText = option.querySelector('.store-selector__option-text');
      if (!optionText) {
        optionText = option.querySelector('span');
      }
      return optionText ? optionText.textContent.trim() : '';
    }
    return '';
  }

  closeDropdown() {
    this.isOpen = false;
    if (this.button) {
      this.button.setAttribute('aria-expanded', 'false');
    }
    if (this.list) {
      this.list.hidden = true;
    }
    
    // Remove active class for new design
    if (this.classList.contains('header-store-switcher')) {
      this.classList.remove('header-store-switcher--active');
    }
  }

  static getPreference() {
    return localStorage.getItem('ygc_store_preference') || '';
  }

  static setPreference(value) {
    if (value && value !== '') {
      localStorage.setItem('ygc_store_preference', value);
    } else {
      localStorage.removeItem('ygc_store_preference');
    }
    
    const storeName = StoreSelector.getStoreName(value);
    StoreSelector.notifySubscribers(value || '');
    
    const event = new CustomEvent('storePreferenceChanged', {
      detail: {
        value: value || '',
        storeName: storeName
      },
      bubbles: true
    });
    document.dispatchEvent(event);
  }

  static subscribers = [];

  static subscribe(callback) {
    if (typeof callback === 'function') {
      StoreSelector.subscribers.push(callback);
      const currentValue = StoreSelector.getPreference();
      callback(currentValue, StoreSelector.getStoreName(currentValue));
    }
  }

  static unsubscribe(callback) {
    const index = StoreSelector.subscribers.indexOf(callback);
    if (index > -1) {
      StoreSelector.subscribers.splice(index, 1);
    }
  }

  static notifySubscribers(value) {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      const storeName = StoreSelector.getStoreName(value);
      StoreSelector.subscribers.forEach(callback => {
        try {
          callback(value || '', storeName);
        } catch (error) {
          console.error('Error in store preference subscriber:', error);
        }
      });
    });
  }

  static getStoreName(value) {
    const selector = document.querySelector('store-selector');
    if (selector && selector.options) {
      const option = Array.from(selector.options).find(
        opt => opt.dataset.storeValue === value
      );
      if (option) {
        let optionText = option.querySelector('.store-selector__option-text');
        if (!optionText) {
          optionText = option.querySelector('span');
        }
        return optionText ? optionText.textContent.trim() : '';
      }
    }
    return '';
  }
}

// Only define custom element if not already defined
if (!customElements.get('store-selector')) {
  customElements.define('store-selector', StoreSelector);
}

// Initialize on DOM ready (only run once)
if (!window.__storeSelectorInitialized) {
  window.__storeSelectorInitialized = true;
  
  function initializeStoreSelector() {
    document.querySelectorAll('[data-store-selector]').forEach(element => {
      if (!element.closest('store-selector')) {
        const selector = document.createElement('store-selector');
        element.parentNode.insertBefore(selector, element);
        selector.appendChild(element);
      }
    });
    
    const currentValue = StoreSelector.getPreference();
    if (currentValue) {
      const event = new CustomEvent('storePreferenceChanged', {
        detail: {
          value: currentValue,
          storeName: ''
        },
        bubbles: true
      });
      document.dispatchEvent(event);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeStoreSelector);
  } else {
    initializeStoreSelector();
  }
}
} // End of StoreSelector class guard
